import { useState } from "react";

import { Card, CardContent } from "@/shared/ui/card";

import type { GeoPlatformDetail, GeoReport } from "../../../api/geoService";
import { ProbeWall } from "../report/ProbeWall";
import { HitPill, Kpi, ProvenanceBadge, rowPill, SecNo } from "./shared";

/** 05 模型提及概览:平台卡阵,徽标由 provenance 判定(唯一真源)。点卡 → 跳 04 对应平台。 */
export function PlatformGrid8({
  report,
  onPick,
}: {
  report: GeoReport;
  onPick?: (index: number) => void;
}) {
  const platforms = report.platform_details?.platforms ?? [];
  if (!platforms.length) return null;
  // 点卡 → 切 07 的 tab 再滚过去。滚动放在 state 更新之后同一帧无所谓(锚点 DOM 一直在),
  // 但**必须** onPick 先调 —— 先滚后切会让用户看到旧平台的明细闪一下。
  const pick = (i: number) => {
    onPick?.(i);
    document.getElementById("geo-sec-platforms")?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <section id="geo-sec-grid" className="space-y-2">
      <SecNo
        no="05"
        title="模型提及概览"
        desc="逐平台:提及率 / 正向率 / 负向率 · 来源徽标 · 点卡看该平台明细"
      />
      <div
        className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4"
        data-testid="geo-platform-grid"
      >
        {platforms.map((p, i) => (
          <Card
            key={p.platform}
            role="button"
            tabIndex={0}
            aria-label={`查看 ${p.label} 的平台详情`}
            onClick={() => pick(i)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                pick(i);
              }
            }}
            className={`cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              p.provenance === "real" ? "ring-1 ring-primary/40" : ""
            }`}
          >
            <CardContent className="p-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold">
                <span className="truncate">{p.label}</span>
                <span className="ml-auto">
                  <ProvenanceBadge provenance={p.provenance} />
                </span>
              </div>
              <div className="text-lg font-extrabold tabular-nums">
                {p.mention_rate}
                <span className="ml-0.5 text-2xs font-normal text-muted-foreground">% 提及</span>
              </div>
              <div className="mt-0.5 flex gap-2 text-2xs text-muted-foreground">
                <span>
                  正向 <b className="text-emerald-600">{p.positive_rate}%</b>
                </span>
                <span>
                  负向 <b className="text-rose-500">{p.negative_rate}%</b>
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {/* 常驻释义:tooltip 要悬停才看得到,而报告是要发给客户的(测试反馈「推演是什么意思?」)。
          实测/推演各有几家由 provenance 统计而来,不写死数字。 */}
      <p className="px-0.5 text-2xs leading-relaxed text-muted-foreground">
        <b className="text-emerald-600">实测</b>
        {`(${report.platform_details?.real_count ?? 0} 家)`}
        =该平台真的被提问并现场作答,逐条留了证据;
        <b className="ml-1.5 text-amber-600">推演</b>
        {`(${report.platform_details?.derived_count ?? 0} 家)`}
        =该平台暂无可用 API 通道,数值由实测结果按其内容生态确定性推算,
        <b>不是真实问答</b>,因此没有原话样本、引用只到域名层;同一家企业重跑结果一致。
      </p>
    </section>
  );
}

function QuestionCol({
  title,
  rows,
}: {
  title: string;
  rows: GeoPlatformDetail["questions"]["positive"];
}) {
  return (
    <div>
      <h6 className="mb-1.5 text-xs font-bold">
        {title}
        <i className="ml-1.5 font-mono text-2xs not-italic text-muted-foreground">
          {rows.length} 条
        </i>
      </h6>
      {rows.map((r) => {
        const p = rowPill(r);
        return (
          <div
            key={r.question}
            className="flex items-start gap-2 border-b border-dashed py-1.5 text-2xs leading-normal last:border-b-0"
          >
            <HitPill kind={p.kind}>{p.text}</HitPill>
            <span className="min-w-0">{r.question}</span>
          </div>
        );
      })}
    </div>
  );
}

/** 04 平台详情:tabs + KPI + 正负问询双列 + 原话样本(只在实测平台)+ 原话墙收编。 */
export function PlatformDetailTabs({
  report,
  active: activeProp,
  onActive,
}: {
  report: GeoReport;
  active?: number;
  onActive?: (index: number) => void;
}) {
  const platforms = report.platform_details?.platforms ?? [];
  // 受控/非受控双模:父组件(GeoReportV2Panel)传 active 时由它说了算(06 点卡能切过来);
  // 不传时退回自管(存量单测直接渲染本组件,不该被迫改)。
  const [ownActive, setOwnActive] = useState(0);
  const active = activeProp ?? ownActive;
  const setActive = (i: number) => {
    setOwnActive(i);
    onActive?.(i);
  };
  if (!platforms.length) return null;
  const cur = platforms[Math.min(active, platforms.length - 1)];
  // 原话样本:当前平台实测问询里取「进推荐名单/提及」优先的一条(推演平台恒无)
  const quote =
    cur.provenance === "real"
      ? [...cur.questions.positive, ...cur.questions.negative]
          .filter((r) => r.answer_excerpt)
          .sort((a, b) => Number(b.is_recommended) - Number(a.is_recommended))[0]
      : undefined;
  return (
    <section id="geo-sec-platforms" className="space-y-2">
      <SecNo no="04" title="平台详情" desc="逐平台问询命中明细 · 原话样本只来自实测平台" />
      <Card>
        <CardContent className="p-6">
          <div className="mb-3 flex flex-wrap gap-1.5" data-testid="geo-platform-tabs">
            {platforms.map((p, i) => (
              <button
                key={p.platform}
                onClick={() => setActive(i)}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-2xs ${
                  i === active
                    ? "border-transparent bg-primary font-bold text-primary-foreground"
                    : "bg-background text-muted-foreground hover:bg-muted"
                }`}
              >
                <span
                  className={`h-[5px] w-[5px] rounded-full ${
                    p.provenance === "real" ? "bg-emerald-500" : "bg-amber-500"
                  }`}
                />
                {p.label}
              </button>
            ))}
          </div>
          <div className="mb-3 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <Kpi value={`${cur.mention_rate}%`} label="提及率" />
            <Kpi value={`${cur.positive_rate}%`} label="正向率" />
            <Kpi value={`${cur.negative_rate}%`} label="负向率" negative />
            {/* 1006:不露内部模型版本号(glm-4.7 等),只给平台中文名;推演平台照旧标「生态推演」 */}
            <Kpi
              value={cur.samples}
              label={`有效样本 · ${cur.provenance === "real" ? cur.label : "生态推演"}`}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <QuestionCol title="正面问询" rows={cur.questions.positive} />
            <QuestionCol title="负面问询" rows={cur.questions.negative} />
          </div>
          {quote ? (
            <div
              className="mt-3 rounded-xl border border-l-[3px] border-l-emerald-500 bg-background px-3.5 py-2.5 text-2xs leading-relaxed"
              data-testid="geo-platform-quote"
            >
              <div className="font-bold">原话样本 · Q:{quote.question}</div>
              <div className="mt-0.5 text-muted-foreground">{quote.answer_excerpt}</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {quote.is_recommended && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-2xs text-muted-foreground">
                    ✓ 进入推荐名单
                  </span>
                )}
                <span className="rounded-full bg-muted px-2 py-0.5 text-2xs text-muted-foreground">
                  情感:
                  {quote.sentiment === "positive"
                    ? "正向"
                    : quote.sentiment === "negative"
                      ? "负向"
                      : "中性"}
                </span>
                {quote.citation_domains.length > 0 && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-2xs text-muted-foreground">
                    引用:{quote.citation_domains.slice(0, 3).join(" · ")}
                  </span>
                )}
                <span className="rounded-full bg-muted px-2 py-0.5 text-2xs text-muted-foreground">
                  证据已留档
                </span>
              </div>
            </div>
          ) : (
            <p className="mt-2.5 text-2xs text-muted-foreground" data-testid="geo-derived-note">
              推演平台:问询命中明细由实测结果生态推演,<b>不展示原话样本</b> —— 推演不伪造回答。
            </p>
          )}
        </CardContent>
      </Card>
      {/* 旧「原话回放墙」收编:全部实测原话可查(内容不丢) */}
      <ProbeWall report={report} />
    </section>
  );
}
